/** Capture (1.N-1 deliverables 15 and 17): the key, the note's text, and filing it as a page. Pure. */

export interface CaptureKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly isComposing?: boolean;
}

/** Mod-Shift-K: Command on Apple platforms, Control elsewhere. */
export function isCaptureKey(event: CaptureKeyEvent, apple: boolean): boolean {
  if (event.isComposing === true || event.altKey || !event.shiftKey) return false;
  if (event.key.toLowerCase() !== 'k') return false;
  return apple ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

export const CAPTURE_MAX = 2000;
export const TITLE_MAX = 80;

/** The note to save: trimmed, or null when blank. */
export function captureText(value: string): string | null {
  const text = value.trim();
  return text === '' ? null : text;
}

/** The first line, at most 80 characters, cut at a word boundary with an ellipsis; never empty. */
export function captureTitle(text: string): string {
  const first = text.split(/\r?\n/).find((line) => line.trim() !== '');
  const line = (first ?? '').trim().replace(/\s+/g, ' ');
  if (line === '') return 'Untitled note';
  if (line.length <= TITLE_MAX) return line;
  const head = line.slice(0, TITLE_MAX - 1);
  const space = head.lastIndexOf(' ');
  const cut = (space > 0 ? head.slice(0, space) : head).trimEnd();
  return `${cut}…`;
}

/** A body of one paragraph per non-empty line; no ids (the save pipeline mints them). */
export function captureBody(text: string): Record<string, unknown> {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return {
    type: 'doc',
    content: lines.map((line) => ({ type: 'paragraph', content: [{ type: 'text', text: line }] })),
  };
}

export const CAPTURE_SAVED = 'Captured to the Inbox.';
export const CAPTURE_FAILED = "The note couldn't be saved. Try again.";
export const CAPTURE_HINT = 'Enter saves. Esc closes; your note is kept until you save it.';
export const FILE_CREATE_FAILED = "The page couldn't be created. Try again.";
export const FILE_DISMISS_FAILED =
  "The page was created, but the note is still in the Inbox. Ignore it permanently when you're done.";

export const filedStatus = (title: string): string => `Filed “${title}”.`;
